// "The work" — repair mix. Sales and profit by category as visual twins, the
// value distribution (click a tier), the damage sections (click a section) and
// labor hours framed as shop load.

import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList } from "recharts"
import { ChapterSection, ChartCard, LegendRow, BarTip, Donut, Highlight } from "../room-chrome"
import { AXIS_TICK, CAT, GRID_STROKE, HIGH_VALUE_FLOOR, RAMP, TECH_WEEK_HOURS, fmt$ } from "../room-model"
import type { Categories, Delivery, Headline, HighlightAction, HighlightFinding, deriveDistribution, deriveLaborHours, deriveProfit, deriveSections } from "../derive"

type Distribution = ReturnType<typeof deriveDistribution>
type Sections = ReturnType<typeof deriveSections>
type LaborHours = ReturnType<typeof deriveLaborHours>
type Profit = ReturnType<typeof deriveProfit>

function CategoryTiles({ rows }: { rows: Categories["rows"] }) {
  return (
    <div className="flex gap-2 flex-wrap mt-4">
      {rows.map(r => (
        <div key={r.key} className="flex-1 min-w-[74px] rounded-lg bg-[#F1F0EB] px-2.5 py-2 border-l-2" style={{ borderColor: r.color }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6E6C64]">{r.name}</p>
          <p className="text-[13px] font-bold tabular-nums text-zinc-900">{fmt$(r.value)}</p>
          <p className="text-[10px] text-[#0F6E56]">{r.margin}% margin</p>
        </div>
      ))}
    </div>
  )
}

export function WorkChapter({ d, categories, profit, distribution, highlight, onHighlight, sectionsData, laborHours, delivery, tier, section, onToggleTier, onToggleSection, rangeLabel }: {
  d: Headline
  categories: Categories
  profit: Profit
  distribution: Distribution
  highlight?: HighlightFinding
  onHighlight: (a: HighlightAction) => void
  sectionsData: Sections
  laborHours: LaborHours
  delivery: Delivery
  tier: string
  section: string
  onToggleTier: (idx: number) => void
  onToggleSection: (key: string) => void
  rangeLabel: string
}) {
  const techWeeks = laborHours.total / TECH_WEEK_HOURS
  const bookedWeeks = delivery.bookedHours / TECH_WEEK_HOURS
  return (
    <ChapterSection id="work" eyebrow="Repair mix" title="Revenue, labor, damage, and parts composition" audience="For the booth">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Sales by category */}
        <ChartCard title="Sales by category"
          sub="Labor · Parts · Paint Materials · Sublet — across the period"
          info="Sublet examples: ADAS scans, calibrations, sensor recalibrations — collision work that routes back to dealership service.">
          {({ expanded }) => (
            <>
              <LegendRow items={categories.rows.map(r => ({ label: r.name, color: r.color }))} />
              <Donut data={categories.rows} center={fmt$(categories.total)} centerSub="Total pipeline" height={expanded ? 300 : 170} />
              <CategoryTiles rows={categories.rows} />
            </>
          )}
        </ChartCard>

        {/* Profit by category — the twin */}
        <ChartCard title="Profit by category"
          sub="Gross profit contribution using category margin assumptions">
          {({ expanded }) => (
            <>
              <LegendRow items={profit.rows.map(r => ({ label: r.name, color: r.color }))} />
              <Donut data={profit.rows} center={fmt$(profit.total)} centerSub="Gross profit" height={expanded ? 300 : 170} />
              <div className="flex gap-2 flex-wrap mt-4">
                {profit.rows.map(r => (
                  <div key={r.key} className="flex-1 min-w-[74px] rounded-lg bg-[#F1F0EB] px-2.5 py-2 border-l-2" style={{ borderColor: r.color }}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6E6C64]">{r.name}</p>
                    <p className="text-[13px] font-bold tabular-nums text-zinc-900">{fmt$(r.value)}</p>
                    <p className="text-[10px] text-[#0F6E56]">{r.margin}% margin</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>

        {/* Value distribution — bars are filters */}
        <ChartCard title="Estimate value distribution" sub="Repair value tiers — click a tier to filter the room">
          {({ expanded }) => (
            <>
              {highlight ? (
                <Highlight text={highlight.text} onClick={highlight.action ? () => onHighlight(highlight.action!) : undefined} />
              ) : (
                <p className="flex items-center gap-1.5 text-[12px] font-semibold text-[#0F6E56] mb-3">
                  <span className="size-2.5 rounded-[3px] shrink-0" style={{ background: RAMP[3] }} />
                  {d.highValueCount} estimate{d.highValueCount !== 1 ? "s" : ""} over ${HIGH_VALUE_FLOOR.toLocaleString()}
                </p>
              )}
              <div style={{ height: expanded ? 360 : 190 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={distribution} margin={{ top: 14 }}>
                    <CartesianGrid vertical={false} stroke={GRID_STROKE} />
                    <XAxis dataKey="name" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                    <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                    <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={expanded ? 80 : 44} className="cursor-pointer"
                      onClick={(entry: { tierIdx?: number }) => entry?.tierIdx != null && onToggleTier(entry.tierIdx)}>
                      <LabelList dataKey="value" position="top" style={{ fontSize: 10, fill: "#6E6C64", fontVariantNumeric: "tabular-nums" }} />
                      {distribution.map(t => <Cell key={t.name} fill={t.color} opacity={t.dim ? 0.3 : 1} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </ChartCard>

        {/* Damage sections — bars are filters */}
        <ChartCard title="Most common damage sections" sub="Line item frequency — click a section to filter the room" className="lg:col-span-2">
          {({ expanded }) => (
            sectionsData.length === 0 ? (
              <p className="h-[200px] grid place-items-center text-sm text-[#8B897E]">No line-item data on these estimates yet.</p>
            ) : (
              <div style={{ height: Math.max(sectionsData.length * (expanded ? 44 : 32) + 30, 120) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sectionsData} layout="vertical" margin={{ left: 24, right: 16 }}>
                    <CartesianGrid horizontal={false} stroke={GRID_STROKE} />
                    <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={AXIS_TICK} tickLine={false} axisLine={false} width={110} />
                    <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="value" name="Line items" radius={[0, 4, 4, 0]} maxBarSize={expanded ? 26 : 18} className="cursor-pointer"
                      onClick={(entry: { key?: string }) => entry?.key && onToggleSection(entry.key)}>
                      {sectionsData.map(s => <Cell key={s.key} fill={CAT.brand} opacity={s.dim ? 0.3 : 1} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )
          )}
        </ChartCard>

        {/* Labor hours — framed as shop load */}
        <ChartCard title="Labor hours by type" sub={`Breakdown across the period · ${rangeLabel}`}>
          {({ expanded }) => (
            <>
              <LegendRow items={laborHours.rows.map(r => ({ label: r.name, color: r.color }))} />
              {laborHours.total === 0 ? (
                <p className="h-[170px] grid place-items-center text-sm text-[#8B897E]">No operation-level labor data yet.</p>
              ) : (
                <>
                  <Donut data={laborHours.rows} center={`${Math.round(laborHours.total)} h`} centerSub="Total hours" height={expanded ? 300 : 170} />
                  <p className="text-[11px] text-[#8B897E] mt-3 text-center">
                    ≈ <strong className="text-zinc-600 tabular-nums">{techWeeks.toFixed(1)}</strong> tech-weeks in this period
                    {delivery.bookedHours > 0 && <> · <strong className="text-[#0F6E56] tabular-nums">{bookedWeeks.toFixed(1)}</strong> tech-weeks booked (confirmed + scheduled)</>}
                    {" "}at {TECH_WEEK_HOURS}h/week
                  </p>
                  {delivery.lowConfidence > 0 && (
                    <p className="text-[11px] text-amber-700 mt-1.5 text-center">
                      <strong className="tabular-nums">{delivery.lowConfidence}</strong> estimate{delivery.lowConfidence !== 1 ? "s" : ""} carry low-confidence AI lines — worth a human look before sending
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </ChartCard>
      </div>
    </ChapterSection>
  )
}
